import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Plus, Trash2, Search, Building2, UserCheck, Calendar, DollarSign, Check } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Collab, createDocument, getDocuments, updateDocument, getDocument } from '../lib/supabaseHelpers';
import { Influencer, normalizeInfluencer } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';
import InfluencerAvatar from '../components/InfluencerAvatar';
import CustomListbox from '../components/CustomListbox';

interface Client {
  id?: string;
  agency_id: string;
  brand_name: string;
  sector: string;
  contacts: any[];
  notes?: string;
}

interface Deliverable {
  type: string;
  quantity: number;
}

const CONTENT_TYPES = [
  { value: 'post_instagram', label: 'Post Instagram' },
  { value: 'story_instagram', label: 'Story Instagram' },
  { value: 'reel_instagram', label: 'Réel Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'Intégration YouTube' },
  { value: 'twitch', label: 'Live Twitch' },
  { value: 'story_snapchat', label: 'Story Snapchat' },
  { value: 'mediatisation', label: 'Médiatisation' },
  { value: 'ugc', label: 'UGC' },
] as const;

const COLLAB_TYPES = [
  { value: 'collaboration_entrante', label: 'Collaboration entrante' },
  { value: 'collaboration_sortante', label: 'Collaboration sortante' },
  { value: 'casting_interne', label: 'Casting interne' },
  { value: 'casting_externe', label: 'Casting externe' },
] as const;

export default function CollaborationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [clients, setClients] = useState<Client[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState<boolean>(false);
  const [isInfluencerSelectorOpen, setIsInfluencerSelectorOpen] = useState<boolean>(false);
  const [clientSearch, setClientSearch] = useState('');
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [isSearchingInfluencers, setIsSearchingInfluencers] = useState(false);
  const [searchResults, setSearchResults] = useState<Influencer[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Partial<Collab>>({
    name: '',
    type: 'collaboration_entrante',
    brand: '',
    date: new Date(),
    signed_date: undefined,
    influencers: [],
    influencer_ids: [],
    quote_sent: false,
    contract_signed: false,
    manager: '',
    post_progress: '0',
    billing_status: 'pending',
    payment_received: false,
    budget: 0,
    quote_number: '',
    invoice_number: '',
  });

  useEffect(() => {
    loadInitialData();
  }, [id, location]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Check if we have an id in the query params (from edit button in details page)
      const urlParams = new URLSearchParams(window.location.search);
      const queryId = urlParams.get('id');
      const collaborationId = queryId || id;
      
      if (!currentUser || !userProfile?.agency_id) return;

      const clientsData = await getDocuments<Client>('clients', userProfile.agency_id);
      setClients(clientsData);
      
      // If editing existing collaboration, load its data
      if (collaborationId && collaborationId !== 'new') {
        const collabData = await getDocument<Collab>('collabs', collaborationId);
        if (collabData) {
          setFormData(collabData);
        } else {
          navigate('/app/collaborations');
        }
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    console.log("Soumission du formulaire", formData);
    console.log("Soumission du formulaire", formData);
    
    try {
      setIsSaving(true);
      
      // Calculer l'ID de la collaboration à partir de l'URL ou du paramètre
      const collaborationId = new URLSearchParams(window.location.search).get('id') || id;
      
      console.log(`Sauvegarde de la collaboration: ${collaborationId || 'nouvelle'}`);
      
      // Créer une copie profonde des données du formulaire pour éviter les références partagées
      const formDataToSave = JSON.parse(JSON.stringify(formData));
      
      if (!currentUser || !userProfile?.agency_id) {
        setError('Utilisateur non connecté');
        setIsSaving(false);
        return;
      }

      // Set agency ID
      formDataToSave.agency_id = userProfile.agency_id;
      formDataToSave.created_by = currentUser.id;
      
      // Validate required fields
      if (!formData.name || !formData.brand || !formData.manager) {
        setError('Veuillez remplir tous les champs obligatoires');
        setIsSaving(false);
        return;
      }

      // Prepare influencer IDs
      if (formData.influencers && formData.influencers.length > 0) {
        const influencerIds = formData.influencers
          .filter(inf => inf.id)
          .map(inf => inf.id as string);

        formDataToSave.influencer_ids = influencerIds;
      }
      
      // Calculate budget from influencers if not set
      if (!formData.budget) {
        const calculatedBudget = formData.influencers?.reduce((total, influencer) => {
          return total + (parseFloat(influencer.rate) || 0);
        }, 0) || 0;
        
        formDataToSave.budget = calculatedBudget;
      }
      
      if (collaborationId && collaborationId !== 'new') {
        console.log("Mise à jour de la collaboration existante:", collaborationId);
        await updateDocument('collabs', collaborationId, formDataToSave);

        setSuccess('Collaboration mise à jour avec succès');

        setTimeout(() => {
          navigate(`/app/collaborations/${collaborationId}`, { replace: true });
        }, 1500);
      } else {
        console.log("Création d'une nouvelle collaboration");
        try {
          const newCollab = await createDocument('collabs', formDataToSave as Collab);

          setSuccess('Collaboration créée avec succès');

          if (newCollab && newCollab.id) {
            setTimeout(() => {
              navigate(`/app/collaborations/${newCollab.id}`);
            }, 1500);
          } else {
            setTimeout(() => {
              navigate('/app/collaborations');
            }, 1500);
          }
        } catch (createError) {
          console.error("Erreur lors de la création:", createError);
          setError('Erreur lors de la création: ' + (createError instanceof Error ? createError.message : 'Erreur inconnue'));
          throw createError;
        }
        
        setSuccess('Collaboration créée avec succès');
      }
    } catch (error) {
      console.error('Error saving collaboration:', error);
      setError('Erreur lors de la sauvegarde de la collaboration: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectClient = (client: Client) => {
    setFormData(prev => ({
      ...prev,
      brand: client.brand_name,
    }));
    setIsClientSelectorOpen(false);
  };

  const handleSelectInfluencer = (influencer: Influencer) => {
    setFormData(prev => ({
      ...prev,
      influencerIds: [...(prev.influencerIds || []), influencer.id!],
      influencers: [
        ...(prev.influencers || []),
        {
          id: influencer.id,
          fullName: influencer.fullName,
          profile_image_url: influencer.profile_image_url,
          deliverables: [],
          rate: '0',
          creator_invoice_sent: false,
          paidPercentage: 0
        },
      ]
    }));
    setIsInfluencerSelectorOpen(false);
    setInfluencerSearch('');
    setSearchResults([]);
  };

  const handleAddDeliverable = (influencerIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        newInfluencers[influencerIndex] = {
          ...newInfluencers[influencerIndex],
          deliverables: [
            ...(newInfluencers[influencerIndex].deliverables || []),
            { type: '', description: '', price: '0' }
          ]
        };
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleRemoveDeliverable = (influencerIndex: number, deliverableIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        newInfluencers[influencerIndex] = {
          ...newInfluencers[influencerIndex],
          deliverables: newInfluencers[influencerIndex].deliverables?.filter((_, i) => i !== deliverableIndex) || []
        };
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleDeliverableChange = (influencerIndex: number, deliverableIndex: number, field: string, value: string) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]?.deliverables?.[deliverableIndex]) {
        newInfluencers[influencerIndex].deliverables[deliverableIndex] = {
          ...newInfluencers[influencerIndex].deliverables[deliverableIndex],
          [field]: value
        };
        
        // Update rate based on deliverables
        if (field === 'price') {
          const totalPrice = newInfluencers[influencerIndex].deliverables.reduce((sum, d) => {
            return sum + parseFloat(d.price || '0');
          }, 0);
          
          newInfluencers[influencerIndex].rate = totalPrice.toString();
        }
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleRemoveInfluencer = (index: number) => {
    const influencerId = formData.influencers?.[index]?.id;
    setFormData(prev => ({
      ...prev,
      influencers: prev.influencers?.filter((_, i) => i !== index) || [],
      influencerIds: prev.influencerIds?.filter(id => id !== influencerId) || []
    }));
  };

  const calculateTotalRate = (deliverables: Deliverable[]) => {
    return deliverables.reduce((total, deliverable) => {
      const price = parseFloat(deliverable.price) || 0;
      return total + price;
    }, 0).toString();
  };

  const updateInfluencerRate = (influencerIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        const totalRate = calculateTotalRate(newInfluencers[influencerIndex].deliverables || []);
        newInfluencers[influencerIndex].rate = totalRate;
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return '';
    
    if (typeof date === 'string') {
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      const d = new Date(date.seconds * 1000);
      return d.toISOString().split('T')[0];
    }
    
    return new Date(date).toISOString().split('T')[0];
  };

  const filteredClients = clients.filter(client =>
    client.brand_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!influencerSearch.trim()) {
      setSearchResults([]);
      setIsSearchingInfluencers(false);
      return;
    }

    setIsSearchingInfluencers(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const searchTerm = `%${influencerSearch.toLowerCase().trim()}%`;

        const { data, error } = await supabase
          .from('influencers')
          .select('id, display_name, full_name, username, profile_image_url, followers_count, engagement_rate, category')
          .eq('archived', false)
          .or(`display_name.ilike.${searchTerm},username.ilike.${searchTerm}`)
          .order('followers_count', { ascending: false })
          .limit(20);

        if (error) throw error;

        setSearchResults((data || []).map(normalizeInfluencer));
      } catch (error) {
        console.error('Error searching influencers:', error);
        setSearchResults([]);
      } finally {
        setIsSearchingInfluencers(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [influencerSearch]);

  const filteredInfluencers = searchResults;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-white/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/collaborations')}
            className="p-2 rounded-xl hover:bg-white/[0.08] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {id && id !== 'new' ? 'Modifier la collaboration' : 'Nouvelle collaboration'}
            </h1>
            <p className="text-white/50">
              {id && id !== 'new' ? 'Mettre à jour les détails de la collaboration' : 'Créer une nouvelle collaboration'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
          {success}
        </div>
      )}

      <form className="space-y-8">
        {/* Informations de base */}
        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl border p-6">
          <h2 className="text-xl font-semibold text-white mb-6">
            Informations de base
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Nom de la collaboration
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                placeholder="Ex: Campagne été 2024"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Type de collaboration
              </label>
              <CustomListbox
                value={formData.type}
                onChange={(value) => setFormData({ ...formData, type: value as Collab['type'] })}
                options={COLLAB_TYPES}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Marque
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.brand}
                  className="w-full pr-10 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  required
                  readOnly
                  onClick={() => setIsClientSelectorOpen(true)}
                  placeholder="Sélectionner un client"
                />
                <button
                  type="button"
                  onClick={() => setIsClientSelectorOpen(true)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-white/40 hover:text-white"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Budget total (€)
              </label>
              <input
                type="number"
                value={formData.budget || ''}
                onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                placeholder="Budget total de la campagne"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Date de signature
              </label>
              <input
                type="date"
                value={formData.signed_date ? formatDate(formData.signed_date) : ''}
                onChange={(e) => setFormData({ ...formData, signed_date: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/[0.08] text-white rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-1">
                Chef de projet
              </label>
              <input
                type="text"
                value={formData.manager}
                onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                required
              />
            </div>
          </div>
        </div>

        {/* Influenceurs */}
        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">
              Influenceurs
            </h2>
            <button
              type="button"
              onClick={() => setIsInfluencerSelectorOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
            >
              <Plus className="w-4 h-4" />
              Ajouter un influenceur
            </button>
          </div>

          <div className="space-y-6">
            {formData.influencers?.map((influencer, influencerIndex) => (
              <div
                key={influencerIndex}
                className="bg-white/[0.04] rounded-xl p-6 border border-white/[0.08]"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <InfluencerAvatar
                      profileImageUrl={influencer.profile_image_url || null}
                      name={influencer.fullName}
                      size="md"
                    />
                    <span className="font-medium text-white">
                      {influencer.fullName}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveInfluencer(influencerIndex)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Tarif global
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={influencer.rate || 0}
                        onChange={(e) => {
                          const newInfluencers = [...(formData.influencers || [])];
                          newInfluencers[influencerIndex] = {
                            ...newInfluencers[influencerIndex],
                            rate: e.target.value
                          };
                          setFormData({ ...formData, influencers: newInfluencers });
                        }}
                        className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                        placeholder="0"
                      />
                      <span className="text-white/50">€</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-white/60">
                        Livrables
                      </label>
                      <button
                        type="button"
                        onClick={() => handleAddDeliverable(influencerIndex)}
                        className="text-sm text-white/50 hover:text-white transition-colors"
                      >
                        + Ajouter un livrable
                      </button>
                    </div>

                    <div className="space-y-3 bg-white/[0.02] rounded-xl p-4 border border-white/[0.06]">
                      {influencer.deliverables?.map((deliverable, deliverableIndex) => (
                        <div key={deliverableIndex} className="grid grid-cols-3 gap-3">
                          <CustomListbox
                            value={deliverable.type}
                            onChange={(value) => handleDeliverableChange(influencerIndex, deliverableIndex, 'type', value)}
                            options={[{ value: '', label: 'Sélectionner un type' }, ...CONTENT_TYPES]}
                          />
                          <input
                            type="text"
                            value={deliverable.description}
                            onChange={(e) => handleDeliverableChange(influencerIndex, deliverableIndex, 'description', e.target.value)}
                            className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                            placeholder="Description"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                             value={deliverable.price ?? ''}
                              onChange={(e) => handleDeliverableChange(influencerIndex, deliverableIndex, 'price', e.target.value)}
                              className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                              placeholder="0"
                            />
                            <span className="text-white/50">€</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveDeliverable(influencerIndex, deliverableIndex)}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {(!influencer.deliverables || influencer.deliverables.length === 0) && (
                        <div className="text-center py-3 text-white/30">
                          Aucun livrable ajouté
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const newInfluencers = [...(formData.influencers || [])];
                        newInfluencers[influencerIndex] = {
                          ...newInfluencers[influencerIndex],
                          creator_invoice_sent: !newInfluencers[influencerIndex].creator_invoice_sent
                        };
                        setFormData({ ...formData, influencers: newInfluencers });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors ${
                        influencer.creator_invoice_sent
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                          : 'bg-white/[0.06] text-white/50 border border-white/[0.08] hover:bg-white/[0.10]'
                      }`}
                    >
                      {influencer.creator_invoice_sent && <Check className="w-4 h-4" />}
                      <span className="text-sm">Facture créateur reçue</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {(!formData.influencers || formData.influencers.length === 0) && (
              <div className="text-center py-8 text-white/30">
                <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                  <UserCheck className="w-6 h-6" />
                </div>
                <p>Aucun influenceur ajouté à cette collaboration</p>
                <p className="text-sm mt-1">Cliquez sur "Ajouter un influenceur" pour commencer</p>
              </div>
            )}
          </div>
        </div>

        {/* Statut et facturation */}
        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl border p-6">
          <h2 className="text-xl font-semibold text-white mb-6">
            Statut et facturation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, quote_sent: !formData.quote_sent })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors ${
                      formData.quote_sent
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                        : 'bg-white/[0.06] text-white/50 border border-white/[0.08] hover:bg-white/[0.10]'
                    }`}
                  >
                    {formData.quote_sent && <Check className="w-4 h-4" />}
                    <span className="text-sm">Devis envoyé</span>
                  </button>
                </label>
                {formData.quote_sent && (
                  <input
                    type="text"
                    value={formData.quote_number}
                    onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                    placeholder="Numéro de devis"
                    className="flex-1 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  />
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, contract_signed: !formData.contract_signed })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors ${
                      formData.contract_signed
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                        : 'bg-white/[0.06] text-white/50 border border-white/[0.08] hover:bg-white/[0.10]'
                    }`}
                  >
                    {formData.contract_signed && <Check className="w-4 h-4" />}
                    <span className="text-sm">Contrat signé</span>
                  </button>
                </label>
              </div>

            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="Numéro de facture"
                  className="flex-1 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_received: !formData.payment_received })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors ${
                      formData.payment_received
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                        : 'bg-white/[0.06] text-white/50 border border-white/[0.08] hover:bg-white/[0.10]'
                    }`}
                  >
                    {formData.payment_received && <Check className="w-4 h-4" />}
                    <span className="text-sm">Paiement reçu</span>
                  </button>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/collaborations')}
            className="px-4 py-2 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Client Selector Modal */}
      <Dialog
        open={isClientSelectorOpen}
        onClose={() => {
          setIsClientSelectorOpen(false);
        }}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsClientSelectorOpen(false)}
                className="p-2 rounded-xl bg-white/[0.08] text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white">
                Sélectionner un client
              </h2>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="w-full pl-10 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectClient(client);
                    }}
                    className="w-full flex items-center p-4 bg-white/[0.04] hover:bg-white/[0.08] rounded-xl transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center mr-3">
                      <Building2 className="w-5 h-5 text-accent-blue" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-white">
                        {client.brand_name}
                      </h3>
                      {client.contacts[0] && (
                        <p className="text-sm text-white/50">
                          {client.contacts[0].name} • {client.contacts[0].email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                {filteredClients.length === 0 && (
                  <div className="text-center py-8 text-white/30">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <p>Aucun client trouvé</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Influencer Selector Modal */}
      <Dialog
        open={isInfluencerSelectorOpen}
        onClose={() => {
          setIsInfluencerSelectorOpen(false);
          setInfluencerSearch('');
          setSearchResults([]);
        }}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsInfluencerSelectorOpen(false)}
                className="p-2 rounded-xl bg-white/[0.08] text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white">
                Sélectionner un influenceur
              </h2>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={influencerSearch}
                  onChange={(e) => setInfluencerSearch(e.target.value)}
                  placeholder="Rechercher un influenceur..."
                  className="w-full pl-10 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {isSearchingInfluencers ? (
                  <div className="text-center py-8 text-white/30">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-white/60 mx-auto mb-3"></div>
                    <p>Recherche en cours...</p>
                  </div>
                ) : !influencerSearch.trim() ? (
                  <div className="text-center py-8 text-white/30">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                      <UserCheck className="w-6 h-6" />
                    </div>
                    <p>Commencez à taper pour rechercher un influenceur</p>
                    <p className="text-sm mt-1">Tapez un nom, pseudonyme ou nom d'utilisateur</p>
                  </div>
                ) : filteredInfluencers.map((influencer) => (
                  <div
                    key={influencer.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectInfluencer(influencer);
                    }}
                    className="w-full flex items-center p-4 bg-white/[0.04] hover:bg-white/[0.08] rounded-xl transition-colors cursor-pointer"
                  >
                    <InfluencerAvatar
                      profileImageUrl={influencer.profile_image_url}
                      name={influencer.fullName || influencer.username}
                      size="md"
                      className="mr-3"
                    />
                    <div className="text-left">
                      <h3 className="font-medium text-white">
                        {influencer.fullName}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-white/50">
                        {influencer.platforms?.length > 0 ? (
                          influencer.platforms.map((platform, index) => (
                            <span key={index}>
                              {platform.name} • {platform.audience}
                            </span>
                          ))
                        ) : (
                          <span>@{influencer.username}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {!isSearchingInfluencers && influencerSearch.trim() && filteredInfluencers.length === 0 && (
                  <div className="text-center py-8 text-white/30">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                      <UserCheck className="w-6 h-6" />
                    </div>
                    <p>Aucun influenceur trouvé</p>
                    <p className="text-sm mt-1">Essayez avec un autre terme de recherche</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}